import React, { Component } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { BarChart as ReBarChart, Bar, ReferenceLine, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea } from 'recharts';
import { defaultNotifier } from 'functions/notificationHandler';
import { THEME_COLOR_SWATCH } from 'utils/constants';
import Card from 'components/Detail/Card';
import { VuiButton } from '@topkamera/viso-ui'
import {
  DEFAULT_CARTESIAN_CHART_CONFIG,
  GROUP_BY_UNIQUE_COLORS,
  getCartesianChartConfig,
  formatDate,
  formatYAxisLabel,
  getThresholdCalculatedValue,
  getActualTimeRange,
  roundData,
  safeParseNumber,
  getChartRenderRequirements,
  normalizePercentData,
  renderSeriesTrendlines,
  filterChartDataByLegends,
  chartDotMenu,
  sanitizeGradientId,
  getThemeThenRandomSeriesColor
} from './utils'
import { isEqual } from 'lodash';
import classNames from 'classnames';
import { ChartLoader } from './ChartLoader';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard';
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants';
import ChartLegend from './ChartLegend';
import ChartTooltip from './ChartTooltip';

class BarChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: {
        groupBy: [],
        data: [],
        uniqueGroupValues: []
      },
      metricColumns: [],
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      cartesianConfig: { ...DEFAULT_CARTESIAN_CHART_CONFIG },
      uniqueGroupByValue: {},
      availableColorIndices: Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys()),
      uniqueGroupByOriginalKey: {},
      isChartDataLoading: false,

      chartConfig: { ...CHART_DEFAULT_VALUES },
      xAxisGroup: '',
      hasMenu: false,
      showOnlySelectedLegend: [],
      refAreaLeft: null,
      refAreaRight: null,
      zoomStartTime: null,
      zoomEndTime: null,
      originalChartData: null,
      isSelecting: false
    };
    this.containerRef = null
  }
  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.height, nextProps.height) ||
      !isEqual(this.props.chartValues, nextProps.chartValues) ||
      !isEqual(this.props.dataStreams, nextProps.dataStreams) ||
      !isEqual(this.props.previewParams, nextProps.previewParams) ||
      !isEqual(this.props.devicesData, nextProps.devicesData) ||
      !isEqual(this.props.chart, nextProps.chart) ||
      this.props.isEditMode !== nextProps.isEditMode ||
      !isEqual(this.state.chartData, nextState.chartData) ||
      !isEqual(this.state.chartConfig, nextState.chartConfig) ||
      this.state.isChartDataLoading !== nextState.isChartDataLoading ||
      !isEqual(this.state.showOnlySelectedLegend, nextState.showOnlySelectedLegend) ||
      this.state.zoomStartTime !== nextState.zoomStartTime ||
      this.state.zoomEndTime !== nextState.zoomEndTime ||
      this.state.refAreaLeft !== nextState.refAreaLeft ||
      this.state.refAreaRight !== nextState.refAreaRight
    );
  }

  handleLegendClick = (legendName) => {
    const { showOnlySelectedLegend } = this.state;

    if (showOnlySelectedLegend.includes(legendName)) {
      this.setState({ showOnlySelectedLegend: showOnlySelectedLegend.filter(item => item !== legendName) })
    }
    else {
      this.setState({ showOnlySelectedLegend: [...this.state.showOnlySelectedLegend, legendName] })
    }
  }

  handleMouseDown = (e) => {
    if (e && e.activeLabel) {
      if (e.nativeEvent) {
        e.nativeEvent.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }
      this.setState({
        refAreaLeft: e.activeLabel,
        isSelecting: true
      });
    }
  }

  handleMouseMove = (e) => {
    if (this.state.isSelecting && e && e.activeLabel) {
      if (e.nativeEvent) {
        e.nativeEvent.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }
      this.setState({ refAreaRight: e.activeLabel });
    }
  }

  handleMouseUp = () => {
    const { refAreaLeft, refAreaRight } = this.state;
    if (refAreaLeft && refAreaRight) {
      const [left, right] = [refAreaLeft, refAreaRight].sort();
      this.setState({
        zoomStartTime: left,
        zoomEndTime: right
      });
    }
    this.setState({
      refAreaLeft: null,
      refAreaRight: null,
      isSelecting: false
    });
  }

  handleResetZoom = () => {
    this.setState({
      zoomStartTime: null,
      zoomEndTime: null,
      refAreaLeft: null,
      refAreaRight: null,
      isSelecting: false
    });
  }

  handleWheel = (e) => {
    e.preventDefault();
    const { chartData, zoomStartTime, zoomEndTime } = this.state;

    if (!chartData?.data || !Array.isArray(chartData.data) || chartData.data.length === 0 || !this.chartContainerRef) return;

    const allData = chartData.data;

    // Use zoom times if set, otherwise use full data range
    const currentStart = zoomStartTime || allData[0]?.bucketed_time;
    const currentEnd = zoomEndTime || allData[allData.length - 1]?.bucketed_time;

    if (!currentStart || !currentEnd) return;

    const zoomFactor = 0.1; // Increased from 0.01 for more noticeable zoom
    const direction = e.deltaY < 0 ? -1 : 1; // Negative deltaY = zoom in (scroll up)

    const currentRange = new Date(currentEnd).getTime() - new Date(currentStart).getTime();
    const zoomAmount = currentRange * zoomFactor * direction;

    const chartRect = this.chartContainerRef.getBoundingClientRect();
    const mouseX = e.clientX - chartRect.left;
    const chartWidth = chartRect.width;
    const mousePercentage = Math.max(0, Math.min(1, mouseX / chartWidth));

    const currentStartTime = new Date(currentStart).getTime();
    const currentEndTime = new Date(currentEnd).getTime();

    // Calculate new range
    let newStartTime = currentStartTime + zoomAmount * mousePercentage;
    let newEndTime = currentEndTime - zoomAmount * (1 - mousePercentage);

    // Ensure we don't zoom beyond the data boundaries
    const dataStartTime = new Date(allData[0]?.bucketed_time).getTime();
    const dataEndTime = new Date(allData[allData.length - 1]?.bucketed_time).getTime();

    newStartTime = Math.max(dataStartTime, newStartTime);
    newEndTime = Math.min(dataEndTime, newEndTime);

    // Ensure minimum zoom range (at least 2 data points worth of time)
    const minRange = 60000; // 1 minute minimum
    if (newEndTime - newStartTime < minRange) {
      return;
    }

    const newStartISO = new Date(newStartTime).toISOString();
    const newEndISO = new Date(newEndTime).toISOString();

    this.setState({
      zoomStartTime: newStartISO,
      zoomEndTime: newEndISO
    });
  }

  async componentDidMount() {
    const isOldChartConfig = typeof this.props.chart?.configuration === 'object' && this.props.chart?.configuration.hasOwnProperty('basicFields')
    this.setState({ hasMenu: !isOldChartConfig })

    if (!this.props.isPreview && !isOldChartConfig) {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      this.setState({ chartConfig: chartConfig })//, () => { console.log('After parsing and saving - this.state.chartConfig: ', this.state.chartConfig)})
    }

    if (this.props.groupByColors) {
      this.setState({
        uniqueGroupByValue: { ...this.props.groupByColors }
      }, () => {
        const newChartData = this.state.chartData.data.map(metric => {
          this.getUniqueGroupByValue(metric.group)

          return {
            ...metric,
            fill: `url(#${this.props.chart.id}_${this.getUniqueGroupByKey(metric.group).replaceAll(' ', '_')}Fill)`
          }
        })
        this.setState({
          chartData: newChartData
        })
      })
    }

    if (this.props.isPreview) {
      this.setState({ chartConfig: this.props.chartValues, hasMenu: false }, () => {
        this.setPreviewData()
      })
    }
  }

  componentWillUnmount() {
    if (this.chartContainerRef) {
      this.chartContainerRef.removeEventListener('wheel', this.handleWheel)
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.isPreview) {
      const currentChartValues = this.props.chartValues
      const prevChartValues = this.state.chartConfig
      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams
      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      // Chart type change - trigger refresh
      if (!isEqual(this.props.chart?.type, prevProps.chart?.type)) {
        return this.setPreviewData()
      }

      // RefreshPreview toggle - trigger refresh (for chart type changes)
      if (!isEqual(this.props.refreshPreview, prevProps.refreshPreview)) {
        return this.setPreviewData()
      }

      // Data streams change - trigger refresh
      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      // Chart values change - update config and potentially refresh
      if (!isEqual(currentChartValues, prevChartValues)) {
        this.setState({ chartConfig: { ...currentChartValues } }, () => {
          // Check if specific properties changed that require refresh
          if (!isEqual(currentChartValues.devices, prevChartValues.devices) ||
            !isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }

      // Devices data change - trigger refresh
      if (!isEqual(prevProps.devicesData, this.props.devicesData)) {
        return this.setPreviewData()
      }

      // Chart or preview params change - trigger refresh
      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }
    }

    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview, chartValues } = this.props

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      if (hasBlockingIssues) {
        return this.setState({ isChartDataLoading: false })
      }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange, dashboardDevices } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      if (hasBlockingIssues) {
        return this.setState({ isChartDataLoading: false })
      }

      // Always use new version raw data flow
      this.setRawChartData()

    } catch (error) {
      this.setState({ isChartDataLoading: false })

      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN].map(r => r.message).join('\n')
    const blockMessages = requirements[BLOCK].map(r => r.message).join('\n')
    const hasWarningIssues = requirements[BLOCK].length > 0
    const hasBlockingIssues = requirements[BLOCK].length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  setChartData = (data, chart) => {
    const { groupBy, metricsData, metricColumns } = data
    const cartesianConfig = chart.configuration && getCartesianChartConfig(chart.configuration)

    const chartData = {
      groupBy: [...groupBy],
      data: [...metricsData],
      uniqueGroupValues: []
    }

    const timeIndex = chartData.groupBy?.indexOf('time')
    const validGroupBy = chartData.groupBy?.filter(item => item !== 'time')[0] || ''

    if (validGroupBy) {
      chartData.data = chartData.data.map(metric => {
        this.getUniqueGroupByValue(metric.group)

        const columnName = metricColumns[0] || ''
        const groupByKey = metric.group[validGroupBy]
        const groupByValue = metric.values[columnName]

        if (!chartData.uniqueGroupValues.includes(groupByKey)) {
          chartData.uniqueGroupValues.push(groupByKey)
        }

        const modifiedGroup = { ...metric.group }
        delete modifiedGroup[validGroupBy]
        const modifiedValues = {
          ...metric.values,
          [groupByKey]: groupByValue
        }
        delete modifiedValues[validGroupBy]

        const modifiedMetric = {
          ...metric,
          group: { ...modifiedGroup },
          values: { ...modifiedValues }
        }

        return {
          ...modifiedMetric,
          fill: `url(#${this.props.chart.id}_${this.getUniqueGroupByKey(metric.group).replaceAll(' ', '_')}Fill)`
        }
      })

      chartData.data = chartData.data.reduce((acc, currMetric) => {
        const existingGroup = acc.find(elem => isEqual(currMetric.group, elem.group))
        if (existingGroup) {
          Object.keys(currMetric.values).forEach(key => {
            if (existingGroup.values[key]) {
              existingGroup.values[key] += currMetric.values[key]
            } else {
              existingGroup.values[key] = currMetric.values[key]
            }
          })
        } else {
          const newMetric = { ...currMetric }
          delete newMetric.fill
          acc.push(newMetric)
        }
        return acc
      }, [])
    } else {
      chartData.data = chartData.data.map(metric => ({ ...metric }))
      this.setState(
        {
          uniqueGroupByValue: {},
          availableColorIndices: Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
        },
        () => {
          if (this.props.onBarColorChange) {
            this.props.onBarColorChange(this.state.uniqueGroupByValue)
          }
        }
      )
    }

    if (timeIndex !== -1) {
      chartData.data = chartData.data.map(metric => ({
        ...metric,
        group: {
          ...metric.group,
          time: formatDate(metric.group.time, cartesianConfig.dateFormatter)
        }
      }))
    }

    chartData.uniqueGroupValues.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

    return this.setState({
      chartData: chartData,
      metricColumns: metricColumns,
      cartesianConfig: cartesianConfig,
      dataErrorMessage: '',
      validData: true
    })
  }

  getStrokeLine = (name, index) => {
    const { chartConfig } = this.state

    const streams = chartConfig?.dataStreams?.streams || []

    // 1) Try to use the stream's configured color by index when present.
    let color = streams[index]?.streamLabel?.color || ''

    // 2) Fallback: deterministic palette order matching legendContent.
    if (!color) {
      color = getThemeThenRandomSeriesColor({ key: name, index, palette: THEME_COLOR_SWATCH })
    }

    return color
  }

  getXAxisInterval = (group) => {
    const { chartConfig } = this.state;
    const xAxisInterval = {
      time: chartConfig.intervalQuantity
    }
    return xAxisInterval[group] || 0;
  }

  getUniqueGroupByKey = (uniqueGroupValue) => {
    let uniqueOriginalValue = Object.entries(uniqueGroupValue)
      .filter(([key, value]) => key !== 'time')
      .map(([key, value]) => value)[0]
    return uniqueOriginalValue
  }

  getUniqueGroupByValue = (uniqueGroupValue) => {
    let availableIndices = [...this.state.availableColorIndices]
    let uniqueValue = ''
    let uniqueOriginalValue = this.getUniqueGroupByKey(uniqueGroupValue)
    const uniqueValueColors = { ...this.state.uniqueGroupByValue }

    if (availableIndices.length === 0) {
      availableIndices = Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
    }

    if (uniqueOriginalValue && (typeof (uniqueOriginalValue) === 'string')) {
      uniqueValue = uniqueOriginalValue.replace(/\s+/g, '_').replace(/[^\w_]/g, '')
      this.setState({
        uniqueGroupByOriginalKey: {
          ...this.state.uniqueGroupByOriginalKey,
          [uniqueValue]: uniqueOriginalValue
        }
      })
    }

    if (!uniqueValueColors.hasOwnProperty(`${uniqueValue}`)) {
      const randomColorIndex = Math.floor(Math.random() * availableIndices.length)
      const colorIndex = availableIndices.splice(randomColorIndex, 1)[0]
      uniqueValueColors[`${uniqueValue}`] = GROUP_BY_UNIQUE_COLORS[colorIndex]
      this.setState({
        uniqueGroupByValue: uniqueValueColors,
        availableColorIndices: availableIndices
      }, () => {
        if (this.props.onBarColorChange) {
          this.props.onBarColorChange(this.state.uniqueGroupByValue)
        }
      })
    }
  }

  hasMetricsFill = () => {
    const { chartData } = this.state

    return chartData &&
      Array.isArray(chartData.data) &&
      chartData.data.length > 0 &&
      chartData.data[0].hasOwnProperty('fill')
  }

  hasValidUniqueGroupBy = () => {
    const { chartData } = this.state

    return !!(chartData.groupBy.filter(item => item !== 'time' && item !== 'bucketed_time')[0] || '')
  }

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, isNewVersion, chartValues, isDefaultTime } = this.props
    const { chartConfig } = this.state

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    let groupByMetrics = chartConfig?.groupBy?.metrics || []

    if (this.props.isPreview) {
      groupByMetrics = chartValues.groupBy.metrics
    }

    // Ensure every configured stream has at least one metric selected
    const streams = chartConfig?.dataStreams?.streams || []
    const anyMissingMetrics = !streams.every(s => Array.isArray(s?.metric?.metrics) && s.metric.metrics.length > 0)
    // Always show loader at the beginning of data computation
    this.setState({ isChartDataLoading: true })
    if (anyMissingMetrics) {
      // Suppress the error on initial dashboard load; keep loader visible until config streams are ready
      if (!this.props.isPreview) {
        return
      }
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: "Select a metric to display the preview",
        validData: false,
        isChartDataLoading: false
      })
    }

    if (groupByMetrics.length === 0) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: "Select a group by to display the chart",
        validData: false,
        isChartDataLoading: false
      })
    }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false
      })
    }

    let data = {}

    const devices = this.getDeviceList();

    if (!devices.length) {
      return this.setState({ isChartDataLoading: false })
    }

    if (this.props.isPreview) {
      this.setState({ isChartDataLoading: true })

      const response = await previewRawChartData({
        dataStreams: this.props.dataStreams,
        visoDevices: devices,
        dashboardId: this.props.dashboardId
      })

      data = {
        errors: response?.data?.errors || null,
        data: response?.data?.data?.previewRawChartData
      }
      this.setState({ isChartDataLoading: false })
    } else {
      this.setState({ isChartDataLoading: true })

      if (devices.length) {
        if (isDefaultTime) {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
            timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
          })

          this.setState({ isChartDataLoading: false })
          data = {
            errors: response?.data?.errors || null,
            data: response?.data?.data?.rawChartData
          }
        } else {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          })

          this.setState({ isChartDataLoading: false })
          data = {
            errors: response?.data?.errors || null,
            data: response?.data?.data?.rawChartData
          }
        }
      }
    }

    if (data.errors) {
      const errorMessage = data.errors?.[0]?.message
      return this.setState({
        dataErrorMessage: errorMessage,
        validData: false,
        isChartDataLoading: false
      })
    }


    if (data.data) {

      // Process multiple streams data structure
      const streamData = data.data || []

      // Extract all unique metric columns (excluding bucketed_time and other group by fields)
      const allMetricColumns = []
      const allGroupByFields = new Set()


      // Get groupBy fields from chartConfig instead of inferring from data
      const groupByFieldsList = (chartConfig?.groupBy?.metrics || [])
        .filter(field => field !== 'bucketed_time' && !field.startsWith('time'))

      streamData.forEach(stream => {
        // Extract metrics - either stream_ fields OR custom label fields
        // IMPORTANT: Filter out groupBy fields to prevent them from appearing as series
        const metrics = stream?.result?.fields?.filter(field =>
          field !== 'bucketed_time' &&
          !field.startsWith('time') &&
          !groupByFieldsList.includes(field)
        ) || []

        allMetricColumns.push(...metrics)
      })

      const uniqueMetricColumns = [...new Set(allMetricColumns)]

      // Check if we have groupBy fields (like hostname, viso_device, etc.)
      const hasGroupByFields = groupByFieldsList.length > 0

      if (hasGroupByFields) {
        // Handle multiple groupBy dimensions - create separate bars for each groupBy value

        // First, collect all bucketed_time values to check if we have actual time data
        const allTimePointsSet = new Set()
        streamData.forEach(stream => {
          const { data: streamRows = [] } = stream.result || {};
          streamRows.forEach(row => {
            if (row.bucketed_time != null && row.bucketed_time !== undefined) {
              allTimePointsSet.add(row.bucketed_time)
            }
          })
        })

        const hasTimeData = allTimePointsSet.size > 0 &&
          Array.from(allTimePointsSet)[0] !== null &&
          Array.from(allTimePointsSet)[0] !== undefined

        if (hasTimeData) {
          // Time-based data with groupBy - use bucketed_time on X-axis
          const timeGroupByMap = new Map()
          const uniqueGroupByValues = new Set()

          uniqueMetricColumns.forEach(metricField => {
            streamData.forEach(stream => {
              if (!stream?.result) return;

              const { data: streamRows = [], fields = [] } = stream.result;
              if (!Array.isArray(streamRows) || !Array.isArray(fields)) return;

              const hasMetric = fields.includes(metricField);
              if (!hasMetric) return;

              streamRows.forEach(row => {
                const timeKey = row.bucketed_time;

                // Create a composite key from all groupBy fields
                const groupByKey = groupByFieldsList.map(field => row[field]).join('_')
                uniqueGroupByValues.add(groupByKey)

                const compositeKey = `${timeKey}_${groupByKey}`

                if (!timeGroupByMap.has(compositeKey)) {
                  timeGroupByMap.set(compositeKey, {
                    bucketed_time: timeKey,
                    groupByKey: groupByKey,
                    groupByValue: groupByFieldsList.length === 1 ? row[groupByFieldsList[0]] : groupByKey
                  });
                }

                const existingRow = timeGroupByMap.get(compositeKey);
                const metricValue = safeParseNumber(row[metricField]);

                existingRow[metricField] = metricValue;
              });
            });
          });

          // Convert to array and restructure for multiple bars
          const allTimePoints = new Set()
          timeGroupByMap.forEach((value) => {
            allTimePoints.add(value.bucketed_time)
          })

          const sortedTimePoints = Array.from(allTimePoints).sort((a, b) => new Date(a) - new Date(b))

          // Restructure data: one row per time point, with columns for each groupByKey_metricField combination
          const chartData = sortedTimePoints.map(timePoint => {
            const dataPoint = {
              bucketed_time: timePoint,
              values: {}
            }

            uniqueGroupByValues.forEach(groupByKey => {
              uniqueMetricColumns.forEach(metricField => {
                const compositeKey = `${timePoint}_${groupByKey}`
                const row = timeGroupByMap.get(compositeKey)
                const value = row?.[metricField] || 0
                const dataKey = `${groupByKey}_${metricField}`
                dataPoint[dataKey] = value
                dataPoint.values[dataKey] = value
              })
            })

            return dataPoint
          })

          // Create unique group values for legend (groupByKey_metricField combinations)
          const uniqueGroupValues = []
          uniqueGroupByValues.forEach(groupByKey => {
            uniqueMetricColumns
              .filter(field => !groupByFieldsList.includes(field))
              .forEach(metricField => {
                uniqueGroupValues.push(`${groupByKey}_${metricField}`)
              })
          })

          this.setState({
            chartData: {
              groupBy: ['bucketed_time', ...groupByFieldsList],
              data: chartData,
              uniqueGroupValues: uniqueGroupValues
            },
            metricColumns: uniqueMetricColumns,
            dataErrorMessage: '',
            validData: true,
          })
        } else {
          // Has groupBy fields but NO time data
          const groupByDataMap = new Map()
          const uniqueGroupByValues = new Set()

          // Get second groupBy field if exists (for multi-groupBy case)
          const secondGroupBy = groupByFieldsList.length > 1 ? groupByFieldsList[1] : null

          uniqueMetricColumns.forEach(metricField => {
            streamData.forEach(stream => {
              const { data: streamRows = [] } = stream.result || {};
              streamRows.forEach(row => {
                // Get first groupBy value for X-axis
                const firstGroupByField = groupByFieldsList[0]
                const firstGroupByValue = row[firstGroupByField]

                // Get second groupBy value for series (if exists)
                const seriesKey = secondGroupBy
                  ? `${row[secondGroupBy]}_${metricField}`
                  : metricField

                if (secondGroupBy) {
                  uniqueGroupByValues.add(row[secondGroupBy])
                }

                if (!groupByDataMap.has(firstGroupByValue)) {
                  groupByDataMap.set(firstGroupByValue, {
                    bucketed_time: firstGroupByValue,  // Use first groupBy as X-axis
                    values: {}
                  })
                }

                const entry = groupByDataMap.get(firstGroupByValue)
                const metricValue = safeParseNumber(row[metricField])
                // Store with composite key for second groupBy case
                entry[seriesKey] = metricValue
                entry.values[seriesKey] = metricValue
              })
            })
          })

          // uniqueGroupValues depends on whether we have second groupBy
          let uniqueGroupValues
          if (secondGroupBy) {
            // Multiple groupBys: series = second groupBy values + metrics
            uniqueGroupValues = []
            const uniqueSecondGroupBy = [...uniqueGroupByValues]
            uniqueSecondGroupBy.forEach(secondVal => {
              uniqueMetricColumns.forEach(metricField => {
                uniqueGroupValues.push(`${secondVal}_${metricField}`)
              })
            })
          } else {
            // Single groupBy: series = metrics only
            uniqueGroupValues = [...uniqueMetricColumns]
          }

          let chartData = Array.from(groupByDataMap.values())
          // Sort by the X-axis value (bucketed_time)
          chartData.sort((a, b) => String(a.bucketed_time || '').localeCompare(String(b.bucketed_time || '')))

          this.setState({
            uniqueGroupByValue: {},  // Clear any groupBy colors from state
            chartData: {
              groupBy: ['bucketed_time'],
              data: chartData,
              uniqueGroupValues: uniqueGroupValues
            },
            metricColumns: uniqueMetricColumns,
            dataErrorMessage: '',
            validData: true,
          })
        }
      } else {
        // No groupBy fields - original behavior (single bar per metric)
        const seriesMap = new Map()
        const allSeriesNames = new Set()

        streamData.forEach(stream => {
          if (!stream?.result) return;

          const { data: streamRows = [], fields = [] } = stream.result;
          if (!Array.isArray(streamRows) || !Array.isArray(fields)) return;

          const metricField = fields.find(field =>
            field !== 'bucketed_time' &&
            !groupByFieldsList.includes(field)
          );
          if (!metricField) return;

          streamRows.forEach(row => {
            const timeKey = row.bucketed_time;
            const seriesName = metricField;

            allSeriesNames.add(seriesName);

            if (!seriesMap.has(seriesName)) {
              seriesMap.set(seriesName, new Map());
            }

            const timeDataMap = seriesMap.get(seriesName);
            const metricValue = safeParseNumber(row[metricField]);

            timeDataMap.set(timeKey, {
              bucketed_time: timeKey,
              [metricField]: metricValue,
              [seriesName]: metricValue
            });
          });
        });

        // Get all unique time points across all series
        const allTimePoints = new Set()
        seriesMap.forEach(timeDataMap => {
          timeDataMap.forEach((_, timeKey) => {
            allTimePoints.add(timeKey)
          })
        })

        const seriesNames = Array.from(allSeriesNames)
        let chartData = []
        let xAxisGroupKeys = []
        let yAxisGroupKeys = seriesNames

        // Check if we have time-based data or just group by data
        if (allTimePoints.size > 0 && Array.from(allTimePoints)[0] !== null && Array.from(allTimePoints)[0] !== undefined) {
          // Time-based data processing
          const sortedTimePoints = Array.from(allTimePoints).sort((a, b) => new Date(a) - new Date(b))

          chartData = sortedTimePoints.map(timeKey => {
            const dataPoint = {
              bucketed_time: timeKey,
              values: {}
            }

            // Add data for each series at this time point
            seriesNames.forEach(seriesName => {
              const timeDataMap = seriesMap.get(seriesName)
              const value = timeDataMap?.get(timeKey)?.[seriesName] || 0
              dataPoint[seriesName] = value
              dataPoint.values[seriesName] = value
            })

            return dataPoint
          })

          xAxisGroupKeys = ['bucketed_time']
        } else {
          // Non-time-based data (e.g., only viso_device grouping)
          // Create one data point per unique groupBy value
          const groupByValueMap = new Map()

          seriesNames.forEach(seriesName => {
            const timeDataMap = seriesMap.get(seriesName)
            timeDataMap.forEach((dataPointInMap, groupByVal) => {
              if (!groupByValueMap.has(groupByVal)) {
                groupByValueMap.set(groupByVal, { values: {} })
              }
              const entry = groupByValueMap.get(groupByVal)
              entry[seriesName] = dataPointInMap[seriesName] || 0
              entry.values[seriesName] = dataPointInMap[seriesName] || 0
              // Use the groupBy value as the x-axis key
              entry[groupByFieldsList[0]] = groupByVal
            })
          })

          chartData = Array.from(groupByValueMap.values())
          xAxisGroupKeys = groupByFieldsList
        }

        this.setState({
          chartData: {
            groupBy: xAxisGroupKeys,
            data: chartData,
            uniqueGroupValues: seriesNames
          },
          metricColumns: yAxisGroupKeys,
          dataErrorMessage: '',
          validData: true,
        })
      }
    }
  }

  onXAxisTickFormat = (val) => {
    return formatDate(val, this.state.chartConfig.chartDateFormat)
  }

  getErrorMessage = () => {
    if (!this.state.validData) {
      return this.state.dataErrorMessage
    }

    if (!this.state.chartConfig.groupBy?.metrics?.length) {
      return "Select a group by to display the chart"
    }

    return ''
  }

  // Render error message
  renderErrorContext = () => {
    return (
      <div className='chart-error'>
        <div>
          {this.getErrorMessage()}
        </div>
        <div>
          {this.state.dataWarnMessage}
        </div>
      </div>
    )
  }

  hasError = () => {
    if (!this.state.validData) {
      return true
    }

    if (!this.state.chartConfig.groupBy?.metrics?.length) {
      return true
    }

    return false
  }

  renderContent = () => {
    const {
      chartConfig,
      metricColumns,
      chartData,
      validData,
      dataErrorMessage,
      uniqueGroupByValue,
      uniqueGroupByOriginalKey,
      dataWarnMessage
    } = this.state;

    const formattedChartData = Array.isArray(chartData?.data) && chartData.data.map((d) => {
      return {
        ...d,
        bucketed_time: d.bucketed_time
      };
    });

    const start = dayjs(formattedChartData?.[0]?.bucketed_time);
    const end = dayjs(formattedChartData?.[formattedChartData?.length - 1]?.bucketed_time);

    const stepMinutes = chartConfig.xAxisStep === 'Auto' ? 15 : chartConfig.xAxisStep; // could be 15, 30, 60...
    const ticks = [];
    for (let t = start; t.isBefore(end) || t.isSame(end); t = t.add(stepMinutes, "minute")) {
      ticks.push(t.toISOString());
    }

    const resolvedStrokeWidth = chartConfig.lineWidth === 'Auto' ? undefined : Number(chartConfig.lineWidth)

    const baseRoundedData = roundData(formattedChartData, chartConfig.decimal);
    const isStackedLayout = chartConfig.chartLayout === 'stacked';
    const processedChartData = (chartConfig.format === 'percent' || chartConfig.format === '%')
      ? normalizePercentData(baseRoundedData, isStackedLayout)
      : baseRoundedData;

    // Filter chart data based on selected legends
    let filteredChartData = filterChartDataByLegends(processedChartData, this.state.showOnlySelectedLegend);

    // Apply zoom filtering
    const { zoomStartTime, zoomEndTime } = this.state;
    if (zoomStartTime && zoomEndTime && filteredChartData) {
      filteredChartData = filteredChartData.filter(
        (dataPoint) => dataPoint.bucketed_time >= zoomStartTime && dataPoint.bucketed_time <= zoomEndTime
      );
      if (filteredChartData.length < 2 && processedChartData?.length >= 2) {
        filteredChartData = processedChartData.slice(0, 2);
      }
    }

    const isLegendRight = chartConfig.legend === 'right';

    const yAxisMin = (chartConfig.yAxisMin !== '' && chartConfig.yAxisMin !== null && chartConfig.yAxisMin !== undefined)
      ? Number(chartConfig.yAxisMin)
      : 0;

    const yAxisMax = (chartConfig.yAxisMax !== '' && chartConfig.yAxisMax !== null && chartConfig.yAxisMax !== undefined)
      ? Number(chartConfig.yAxisMax)
      : ((chartConfig.format === 'percent' || chartConfig.format === '%') ? 100 : 'auto');

    return (
      <>
        {
          this.hasError()
            ?
            this.renderErrorContext()
            :
            <div className=''>
              <div className='grid-row'>
                <div className='width-100'>
                  <div className='grid-col'>
                    <div className='job-training__title job-training__title2'>{this.props.chartValues?.chartTitle || this.props.title}</div>
                  </div>
                </div>

                <div
                  className={classNames(
                    'dashboard-chart',
                    chartConfig.legend !== 'none' && `dashboard-chart--${isLegendRight ? 'right' : 'bottom'}`
                  )}
                  style={{ height: this.props.height + 25 }}
                >
                  <div
                    ref={(el) => { this.containerRef = el }}
                    className={classNames(
                      'dashboard-chart__content',
                      chartConfig.legend !== 'none' && `dashboard-chart__content--${isLegendRight ? 'right' : 'bottom'}`
                    )}
                  >
                    {chartConfig.enableYAxisTitle &&
                      <div className='job-training__chart_yTitle'>
                        {chartConfig.yAxisTitle}
                      </div>
                    }
                    <div
                      ref={(el) => {
                        if (this.chartContainerRef && this.chartContainerRef !== el) {
                          this.chartContainerRef.removeEventListener('wheel', this.handleWheel);
                        }
                        this.chartContainerRef = el;
                        if (el) {
                          el.addEventListener('wheel', this.handleWheel, { passive: false });
                        }
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      style={{ width: "100%", height: "100%", overflow: (isLegendRight || chartConfig.legend === 'below') ? "visible" : "hidden", touchAction: 'none' }}
                    >
                      <ReBarChart
                        responsive
                        data={filteredChartData}
                        style={{ width: '100%', height: '100%' }}
                        margin={{ top: 5, right: 5, left: 20, bottom: 0 }}
                        onMouseDown={this.handleMouseDown}
                        onMouseMove={this.handleMouseMove}
                        onMouseUp={this.handleMouseUp}
                        onMouseLeave={this.handleMouseUp}
                      >
                        <CartesianGrid strokeDasharray="3 3" />

                        {chartData?.groupBy?.length > 0 && (
                          <XAxis
                            type="category"
                            allowDuplicatedCategory={false}
                            dataKey="bucketed_time"
                            padding={{ left: 0, right: 0 }}
                            tickFormatter={(val) => {
                              if (!chartConfig?.groupBy?.metrics?.some(m => /time/i.test(m))) {
                                return val
                              }
                              return this.onXAxisTickFormat(val)
                            }}
                            interval={chartConfig?.groupBy?.metrics?.some(m => /time/i.test(m)) ? Math.ceil(processedChartData.length / 8) : 0}
                            height={chartConfig.labelRotation === '0' || chartConfig.labelRotation === 'off' || chartConfig.labelRotation === 'auto' ? 50 : 80}  // more space for vertical labels
                            tick={
                              (chartConfig.labelRotation === '0' || chartConfig.labelRotation === 'auto')
                                ? { textAnchor: 'middle' }
                                : { textAnchor: 'start' }
                            }
                            angle={
                              chartConfig.labelRotation === 'auto'
                                ? undefined
                                : (chartConfig.labelRotation === 'off' ? 0 : Number(chartConfig.labelRotation))
                            }
                          />)}

                        {chartConfig.axisStep !== 0 &&
                          <YAxis
                            tickMargin={8}
                            tickCount={
                              chartConfig.axisStep === 0 || chartConfig.axisStep === '' || chartConfig.axisStep === null || chartConfig.axisStep === undefined
                                ? undefined
                                : Number(chartConfig.axisStep)
                            }
                            allowDataOverflow={true}
                            domain={[yAxisMin, yAxisMax]}
                            allowDecimals={chartConfig.decimal != 0}
                            tickFormatter={(tick) =>
                              `${chartConfig.prefix || ''}${formatYAxisLabel(tick, chartConfig.format, chartConfig.decimal)} ${chartConfig.suffix || ''}`
                            }
                          />
                        }
                        <Tooltip
                          wrapperStyle={{ visibility: 'hidden' }}
                          cursor={{ fill: "transparent" }}
                          content={(props) => (
                            <ChartTooltip
                              title='Groups'
                              rechartsProps={props}
                              chartContentRef={this.containerRef}
                              dashboardProps={{
                                chartConfig,
                                chartData,
                                uniqueGroupByValue
                              }}
                            />
                          )}
                        />
                        {chartConfig?.enableThreshold &&
                          <ReferenceLine
                            y={getThresholdCalculatedValue(
                              { ...chartConfig.thresholdConfig, type: chartConfig.thresholdType },
                              chartData.data,
                              '',
                              this.hasValidUniqueGroupBy(),
                              uniqueGroupByOriginalKey
                            )}
                            label={{ value: chartConfig?.thresholdName, position: "insideBottomLeft", offset: 10, fontSize: 12, className: 'threshold' }}
                            stroke={chartConfig?.thresholdConfig?.color}
                          />
                        }

                        {renderSeriesTrendlines(chartConfig, chartData, processedChartData, this.getStrokeLine)}

                        {/* Render all gradient definitions first */}
                        <defs>
                          {(chartData.uniqueGroupValues || []).map((item, index) => {
                            const fillId = sanitizeGradientId(`${this.props.chart.id}_${item}`)
                            const stroke = this.getStrokeLine(item, index)
                            return (
                              <linearGradient key={fillId} id={`${fillId}Fill`} x1='0' y1='0' x2='0' y2='1'>
                                <stop stopColor={stroke} stopOpacity={0.7} />
                                <stop offset='1' stopColor={stroke} stopOpacity={0.3} />
                              </linearGradient>
                            )
                          })}
                        </defs>

                        {(chartData.uniqueGroupValues || []).map((item, index) => {
                          const fillId = sanitizeGradientId(`${this.props.chart.id}_${item}`)
                          const stroke = this.getStrokeLine(item, index)
                          return (
                            <Bar
                              key={index}
                              data={filteredChartData}
                              dataKey={(d) => d?.[item] || d?.values?.[item]}
                              barSize={resolvedStrokeWidth}
                              name={item}
                              className='recharts-rectangle-bar1'
                              stackId={chartConfig.chartLayout === 'stacked' ? 'stack' : undefined}
                              dot={false}
                              fill={`url(#${fillId}Fill)`}
                            />
                          )
                        })}
                        {this.state.refAreaLeft && this.state.refAreaRight && (
                          <ReferenceArea
                            x1={this.state.refAreaLeft}
                            x2={this.state.refAreaRight}
                            strokeOpacity={0.3}
                            fill="rgba(128, 128, 128, 0.2)"
                            fillOpacity={0.3}
                          />
                        )}
                      </ReBarChart>
                    </div>
                  </div>

                  {chartConfig.xAxisTitle && chartConfig.enableXAxisTitle &&
                    <div className='job-training__chart_xTitle'>
                      {chartConfig.xAxisTitle}
                    </div>
                  }
                  {chartConfig.legend !== 'none' && (
                    <div
                      className={classNames(
                        'dashboard-chart__legend',
                        `dashboard-chart__legend--${isLegendRight ? 'right' : 'bottom'}`
                      )}
                    >
                      <ChartLegend
                        chartConfig={chartConfig}
                        chartData={chartData}
                        metricColumns={metricColumns}
                        align={chartConfig.legend === 'right' ? 'right' : 'center'}
                        selectedLegends={this.state.showOnlySelectedLegend}
                        onLegendClick={this.handleLegendClick}
                      />
                    </div>
                  )}
                </div>
              </div>
              {dataWarnMessage && <div className='chart-error'>
                {dataWarnMessage}
              </div>}
            </div >
        }
      </>
    );
  }

  render() {
    const { chart, color, isLegacy, isEditMode } = this.props;
    const isZoomed = !!(this.state.zoomStartTime || this.state.zoomEndTime);

    return (
      <Card
        noGrid
        color={this.props.color}
        menu={this.state.hasMenu && !isLegacy ? 'dot' : ''}
        chartId={!this.state.isChartDataLoading && (chart?.id || '66dece43216604577e3ab1a0')}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview chart-cards", {
          "charts-card-preview": this.props.isPreview
        })}
        height={this.props.isPreview && this.props.height + 65}
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        dropDownMenu={this.state.hasMenu && !isLegacy && this.props.onEdit && chartDotMenu({
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}
        customMenuAction={
          isZoomed && (
            <div className="charts-card-chart__menu-action"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              style={{ pointerEvents: 'auto', zIndex: 999 }}>
              <VuiButton
                iconRight='sync'
                label='Reset'
                iconSize='sm'
                color='label'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.handleResetZoom();
                }}
              />
            </div>
          )
        }
      >
      </Card>
    );
  }
}

BarChart.propTypes = {
  onEdit: PropTypes.func,
  model: PropTypes.object,
  chart: PropTypes.any,
  previewParams: PropTypes.any,
  dashboardDevices: PropTypes.any,
  dashboardTimeRange: PropTypes.any,
  height: PropTypes.any,
  isPreview: PropTypes.bool,
  refreshPreview: PropTypes.bool,
  onBarColorChange: PropTypes.func,
  groupByColors: PropTypes.object
};

export default BarChart;
